import type { InjectionKey, Ref } from 'vue'
import type { AgencyStructureNode } from '../AgencyStructureNode'
import type {
  AddAgencyRequest,
  AddAgencyGroupRequest,
  ChangeAgencyGroupNameRequest,
  MoveAgencyGroupRequest,
  GetAgencyStructureRequestParams
} from '../api-requests/AgencyRequests'

/**
 * Business-logic commands for the agency-structure tree. Each command owns its
 * full write cycle — command-id generation, the API call, and the refetch of
 * the structure tree. Views call these instead of issuing API requests and
 * orchestrating the refetch themselves.
 */
export interface AgencyStructureCommands {
  addAgency(payload: AddAgencyRequest): Promise<void>
  addAgencyGroup(payload: AddAgencyGroupRequest): Promise<void>
  changeGroupName(
    agencyGroupId: string,
    payload: ChangeAgencyGroupNameRequest
  ): Promise<void>
  moveGroup(
    agencyGroupId: string,
    payload: MoveAgencyGroupRequest
  ): Promise<void>
  removeGroup(agencyGroupId: string): Promise<void>
}

export interface AgencyStructureService {
  agencyStructure: Ref<AgencyStructureNode[]>
  isLoading: { agencyStructure: boolean }
  fetchAgencyStructure(
    params?: GetAgencyStructureRequestParams & {
      forceFetch?: boolean
      subscribeToEvents?: boolean
    }
  ): Promise<AgencyStructureNode[]>
  clearData(options?: { unsubscribeFromEvents?: boolean }): void
  subscribeToEvents(): () => void
  unsubscribeFromEvents(): void
  commands: AgencyStructureCommands
}

export const agencyStructureKey = Symbol(
  'gs-agency-structure'
) as InjectionKey<AgencyStructureService>
